import React, { useEffect, useState } from 'react';
import { Sparkles, X, Copy, Loader2 } from 'lucide-react';
import { aiAssistAPI } from '../services/api/aiAssistAPI';
import { useToast } from '../hooks/use-toast';

/**
 * AI Assist modal — streams either a client email draft or a kitchen BEO brief
 * from the backend, letting the user copy the result to clipboard.
 *
 * Props:
 *   mode: 'email' | 'brief'
 *   contract: contract object (needs .id)
 *   onClose: () => void
 */
const AIAssistModal = ({ mode, contract, onClose }) => {
  const { toast } = useToast();
  const [emailType, setEmailType] = useState('contract_sent');
  const [customInstructions, setCustomInstructions] = useState('');
  const [output, setOutput] = useState('');
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);

  const isEmail = mode === 'email';
  const title = isEmail ? 'AI Draft Client Email' : 'AI Kitchen Brief';

  const run = async () => {
    setOutput('');
    setDone(false);
    setRunning(true);
    try {
      const res = isEmail
        ? await aiAssistAPI.draftEmail({
            contract_id: contract.id,
            email_type: emailType,
            custom_instructions: customInstructions || undefined,
          })
        : await aiAssistAPI.beoSummary({ contract_id: contract.id });
      setOutput(res.text || '');
      setDone(true);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || String(e);
      toast({ title: 'AI error', description: msg, variant: 'destructive' });
    } finally {
      setRunning(false);
    }
  };

  // Auto-run for brief mode (no options needed)
  useEffect(() => {
    if (!isEmail) run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copy = () => {
    navigator.clipboard.writeText(output);
    toast({ title: 'Copied to clipboard' });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="ai-assist-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b bg-gradient-to-r from-[#1e3a5f] to-[#2d5016] text-white rounded-t-xl">
          <h3 className="font-semibold flex items-center gap-2">
            <Sparkles size={16} /> {title}
            <span className="text-[10px] font-normal opacity-70 ml-1">gpt-5.4</span>
          </h3>
          <button type="button" onClick={onClose} className="opacity-80 hover:opacity-100">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-3 overflow-auto">
          {isEmail && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-[#1e3a5f]">Email Type</label>
                <select
                  value={emailType}
                  onChange={(e) => setEmailType(e.target.value)}
                  disabled={running}
                  data-testid="ai-email-type-select"
                  className="mt-1 w-full h-9 px-2 text-sm border rounded"
                >
                  <option value="contract_sent">Contract sent — next steps</option>
                  <option value="deposit_received">Deposit received — thank you</option>
                  <option value="week_of_reminder">Week-of reminder</option>
                  <option value="thank_you">Post-event thank you</option>
                  <option value="custom">Custom instructions…</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#1e3a5f]">Extra instructions (optional)</label>
                <input
                  value={customInstructions}
                  onChange={(e) => setCustomInstructions(e.target.value)}
                  disabled={running}
                  data-testid="ai-custom-instructions-input"
                  placeholder="e.g. Mention we swapped the salmon for chicken"
                  className="mt-1 w-full h-9 px-2 text-sm border rounded"
                />
              </div>
            </div>
          )}

          <div className="border rounded-lg bg-gray-50 p-3 min-h-[220px] max-h-[50vh] overflow-auto">
            {output ? (
              <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800" data-testid="ai-output">{output}</pre>
            ) : running ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 size={14} className="animate-spin" /> Generating…
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">
                {isEmail ? 'Pick an email type and click Generate.' : 'Preparing brief…'}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-2 px-5 py-3 border-t bg-gray-50 rounded-b-xl">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded border hover:bg-gray-100"
          >Close</button>
          {done && output && (
            <button
              type="button"
              onClick={copy}
              data-testid="ai-copy-btn"
              className="px-3 py-1.5 text-sm rounded border border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white inline-flex items-center gap-1"
            >
              <Copy size={12} /> Copy
            </button>
          )}
          <button
            type="button"
            onClick={run}
            disabled={running}
            data-testid="ai-run-btn"
            className="px-3 py-1.5 text-sm rounded bg-[#c9a227] hover:bg-[#b08f1f] text-[#1e3a5f] font-semibold disabled:opacity-60 inline-flex items-center gap-1"
          >
            <Sparkles size={12} /> {running ? 'Generating…' : done ? 'Regenerate' : 'Generate'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIAssistModal;
